<template>

<app-content :loading="is.loading">

	<app-content-head :title="(isNew) ? 'Add assignment' : 'Manage Assignment'" :back="backName" :backParams="backParams" :readonly="$authorised('con/volunteers/access', 'read')" v-if="!is.loading">

		<app-content-head-actions-item v-if="canSlot" :disabled="changedSlot" icon="volunteers.slots" text="Re-slot" v-on:click="onChangeClick" tooltip="Change slot" />

		<app-content-head-actions-item v-if="isNew || changedSlot" :disabled="!isDirty" icon="undo" text="Undo" v-on:click="onUndoClick" />

		<app-content-head-actions-item v-if="isNew || changedSlot" :loading="is.saving" :disabled="!isDirty || !isValid" icon="save" text="Save" v-on:click="onSaveClick" />

		<app-content-head-actions-item v-if="canAccept" theme="green" icon="accept" text="Accept" v-on:click="onAcceptClick" tooltip="Accept assignment" />

		<app-content-head-actions-item v-if="canReject" theme="red" icon="reject" text="Reject" v-on:click="onRejectClick" tooltip="Reject assignment" />

		<app-content-head-actions-item v-if="canRestore" icon="restore" text="Restore" v-on:click="onRestoreClick" tooltip="Restore assignment" />

		<app-content-head-actions-item v-if="canCancel" theme="red" icon="cancel" text="Cancel" v-on:click="onCancelClick" tooltip="Cancel assignment" />

	</app-content-head>

	<app-content-body v-if="is.initialised">
 
		<app-content-summary v-if="!isNew">

			<app-content-summary-item :is-fixed="true" label="Status" :tag="$constants.volunteers.assignment.statusName[item.status]" :status="$constants.volunteers.assignment.statusTheme[item.status]" :theme="$constants.volunteers.assignment.statusTheme[item.status]" />

			<app-content-summary-item :is-fixed="true" label="Reference" :value="item.reference" />

			<app-content-summary-item :grow="true" label="Volunteer" type="convention/badge" :item="item.badge" />

			<app-content-summary-item :grow="true" label="Role" type="convention/volunteers/role" :item="item.role" />

			<app-content-summary-item :grow="true" label="Slot" type="convention/volunteers/slot" :item="slot" />

			<app-content-summary-item label="Confirmed on" :value="item.date.confirmed | localDate('MM/DD/YYYY')" v-if="item.date.confirmed" />

			<app-content-summary-item label="Cancelled on" :value="item.date.cancelled | localDate('MM/DD/YYYY')" v-if="item.date.cancelled" />
			
		</app-content-summary>

		<app-content-alert v-if="model.slot !== item.slot.id && !isNew" theme="yellow" :is-inline="true" icon="warning">You have changed the new slot of this assignment to <b>{{ slot.name }}</b>. Remember to click Save to finalise the change.</app-content-alert>

		<app-content-box v-if="isNew" :readonly="$authorised('con/volunteers/access', 'read')">

			<app-input-suggest 
				label="Badge" 
				v-model="model.badge" 
				placeholder="Search badges..." 
				:exclude="references.exclude" 
				:locked="!isNew" 
				api="convention/badge" 
				:validation="$v.model.badge" 
			/>
			
			<app-input-suggest 
				label="Role" 
				v-model="model.role" 
				placeholder="Search roles..." 
				api="convention/volunteers/role" 
				:validation="$v.model.role"
			/>
			
			<app-input-picker 
				label="Slot" 
				v-model="model.slot" 
				:text="slot.name"
				placeholder="Select slot"
				v-on:picker="is.picking = $event" 
				:disabled="!isScheduleRole || !model.badge"
			/>
			
		</app-content-box>

	</app-content-body>

	<app-slots v-if="is.picking" v-on:close="is.picking = false" :is-picking="is.picking" :days="references.days" :role="role" :slots="slots" :assigned="assigned" v-on:confirmed="model.slot = $event" />

</app-content>

</template>

<script>

import mixForm from '@/mixin/form'
import { required, requiredIf } from 'vuelidate/lib/validators'

export default {

	mixins: [mixForm],

	data: function() {

		return {
			readonly: this.$authorised('con/volunteers/access', 'read'),
			references: {
				roles: [],
				days: [],
				slots: [],
				assigned: []
			},
			is: {
				picking: false
			},
			model: {
				id: false,
				role: false,
				badge: false,
				slot: false
			}
		}

	},

	validations: {
		model: {
			badge: {
				required
			},
			role: {
				required
			},
			slot: {
				required: requiredIf('requiresSlot')
			}
		}
	},

	computed: {

		changedSlot: function() {
		
			return this.model.slot !== this.item.slot.id

		},

		isScheduleRole: function() {
		
			return (this.role) ? this.role.type === this.$constants.volunteers.role.type.schedule : false
		
		},

		backParams: function() {
		
			if (this.$route.query.redirect === 'badge') {
			
				return {
					id: this.$route.query.badge
				}
			
			} else {
			
				return false

			}

		},

		backName: function() {

			if (this.$route.query.redirect === 'badge') {
			
				return 'Convention.Volunteers.Badges.Edit'
			
			} else {
			
				return this.$route.meta.back

			}

		},

		isPending: function() {
		
			return this.item.status === this.$constants.volunteers.assignment.status.pending

		},

		isRejected: function() {
		
			return this.item.status === this.$constants.volunteers.assignment.status.rejected

		},

		isCancelled: function() {
		
			return this.item.status === this.$constants.volunteers.assignment.status.cancelled

		},

		isAccepted: function() {
		
			return this.item.status === this.$constants.volunteers.assignment.status.confirmed

		},

		canRestore: function() {
		
			return !this.isNew && (this.isCancelled || this.isRejected)

		},

		canAccept: function() {
		
			return !this.isNew && this.isPending

		},

		canReject: function() {
		
			return !this.isNew && this.isPending

		},

		canCancel: function() {
		
			return !this.isNew && this.isAccepted

		},

		canSlot: function() {
		
			return !this.isNew && this.isAccepted

		},

		assigned: function() {
		
			return this.$_.where(this.references.assigned, {
				badge: this.model.badge
			})

		},

		slots: function() {
		
			return this.$_.where(this.references.slots, {
				role: this.model.role
			})

		},

		slot: function() {
		
			return (this.model.slot) ? this.$_.findWhere(this.references.slots, {
				id: this.model.slot
			}) : {}
		
		},

		role: function() {
		
			return this.$_.findWhere(this.references.roles, {
				id: this.model.role
			})
		
		},

		requiresSlot: function() {

			return this.role.type === this.$constants.volunteers.role.type.schedule

		},
	},

	methods: {
	
		onAcceptClick: function() {
		
			this.$router.push({
				name: this.$route.meta.acceptRoute,
				query: this.$route.query
			})

		},
	
		onRejectClick: function() {
		
			this.$router.push({
				name: this.$route.meta.rejectRoute,
				query: this.$route.query
			})

		},
	
		onChangeClick: function() {

			this.is.picking = {
				value: this.model.slot
			}

		},
	
		onCancelClick: function() {
		
			this.$router.push({
				name: this.$route.meta.cancelRoute,
				query: this.$route.query
			})

		},

		onRestoreClick: function() {
		
			this.$router.push({
				name: this.$route.meta.restoreRoute,
				query: this.$route.query
			})
		
		}
	
	}

}

</script>

<style scoped>

</style>